import React, {useState} from "react";
import PropTypes from "prop-types";
import {Box, IconButton, Modal, Tooltip} from "@mui/material";
import {insertNotification} from "Utils/NotificationsUtils";
import ConfirmationModal from "Components/Common/ConfirmationModal";
import CircleIcon from "@mui/icons-material/Circle";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddToPhotosIcon from "@mui/icons-material/AddToPhotos";
import {useTheme} from "styled-components";
import {useIsDesktop} from "Services/ServicesUtils";
import GSPSPropDrivenTable from "Components/DataTable/GSPSPropDrivenTable";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import {AnnotationType} from "Models/Audit/AuditConstants";
import {convertToEst} from "Services/TimeZonesUtils";
import {mediaModalStyle} from "Pages/Audits/AuditTable.styles";
import {SVPToolTipIcon} from "Components/ToolTip/SVPToolTipIcon";
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";
import {ArrowRightAlt} from "@mui/icons-material";

export const AuditTable = ({
    items,
    headerName,
    elementType,
    dimensionHeader,
    onDelete,
    onEdit,
    onAddMedia,
    isViewOnly = true,
    isPublicAudit = false,
}) => {
    const [modalShow, setModalShow] = useState(false);
    const [videoModalShow, setVideoModalShow] = useState(null);
    const [imageModalShow, setImageModalShow] = useState(null);
    const [idToDelete, setIdToDelete] = useState(null);
    const theme = useTheme();
    const isDesktop = useIsDesktop();
    const handleDelete = async () => {
        try {
            await onDelete(idToDelete);
            setModalShow(false);
            setIdToDelete(null);
            if (elementType === AnnotationType.AREA) {
                insertNotification("Success", "Area has been deleted.", "Success");
            }
        } catch (error) {
            insertNotification("Error", `Can't delete area. Reason: ${error.message}`, "error");
        }
    };

    function renderNotesCol() {
        return {
            field: "notes",
            sortable: false,
            ...(isDesktop ? {flex: 3} : {width: 200}),
            headerClassName: "header",
            renderHeader: () => (
                <span>
                    Notes
                    <SVPToolTipIcon
                        fontSize="20px"
                        placement="top"
                        message="This description box is visible only to people inside your organization."
                        rightMargin = "5px"
                    />
                </span>
            ),
            renderCell: (params) => params.value ? params.value : "-",
        };
    }

    function renderDimension() {
        return {
            field: `${elementType === "line" ? "length" : elementType}_feet`,
            headerName: dimensionHeader,
            width: 100,
            headerClassName: "header",
            renderCell: (params) => {
                const value = params.value || 0; // default value
                return parseFloat(value).toFixed(2);
            },
        };
    }

    function renderActions() {
        return {
            field: "actions",
            headerName: "Actions",
            width: 100,
            headerAlign: "right",
            headerClassName: "header",
            renderCell: (params) => <div className="actions w-100 d-flex justify-content-end">
                <button className="border-0 bg-transparent px-2" onClick={() => {
                    setIdToDelete(params.row.id);
                    setModalShow(true);
                }}>
                    <Tooltip title="Delete">
                        <DeleteIcon fontSize="small" sx={{color: theme.color.textOnBg}} />
                    </Tooltip>
                </button>
                <button className="border-0 bg-transparent px-0 ps-2" onClick={() => onEdit(params.row)}>
                    <Tooltip title="Edit">
                        <EditIcon fontSize="small" sx={{color: theme.color.textOnBg}} />
                    </Tooltip>
                </button>
                <button className="border-0 bg-transparent px-0 ps-2" onClick={() => onAddMedia(params.row)}>
                    <Tooltip title="Add Media">
                        <AddToPhotosIcon fontSize="small" sx={{color: theme.color.textOnBg}} />
                    </Tooltip>
                </button>
            </div>,
        };
    }

    function renderImages(images, params) {
        if (images && images.length > 0) {
            return (
                <div className="d-flex flex-wrap">
                    {images.slice(0, 2).map((image, index) =>
                        image.image_value && (
                            <div key={`${index}`}>
                                <button
                                    key={`${params.row.id}-${index}`}
                                    className="imgContainer border-0 bg-transparent mx-3 my-2 p-0"
                                    style={{width: "120px"}}
                                    onClick={() => setImageModalShow(
                                        imageModalShow === image.image_value ? null : image.image_value,
                                    )}
                                >
                                    <img
                                        src={image.image_value}
                                        className="img-fluid rounded-1"
                                        alt="audit thumbnail"
                                        style={{
                                            width: "120px",
                                            height: "90px",
                                            objectFit: "contain",
                                            backgroundColor: "#f8f9fa",
                                        }}
                                    />
                                    {convertToEst(image.created_at, true)}
                                </button>
                                <Modal
                                    open={imageModalShow === image.image_value}
                                    onClose={() => setImageModalShow(null)}
                                >
                                    <Box sx={mediaModalStyle}>
                                        <IconButton
                                            aria-label="close"
                                            size="large"
                                            onClick={() => setImageModalShow(null)}
                                            sx={{
                                                position: "absolute",
                                                top: "12px",
                                                right: "15px",
                                                zIndex: "5000",
                                                color: theme.color.bodyBackground,
                                            }}
                                        >
                                            <CloseRoundedIcon
                                                sx={{
                                                    color: "red",
                                                    transform: "scale(1.4)",
                                                    stroke: "#333",
                                                    strokeWidth: 1.4,
                                                }}
                                            />
                                        </IconButton>
                                        <img
                                            src={image.image_value}
                                            alt="Preview"
                                            className="w-100 rounded"
                                            style={{maxHeight: "80vh", objectFit: "contain"}}
                                        />
                                    </Box>
                                </Modal>
                            </div>
                        ))}
                </div>
            );
        } else {
            return "-";
        }
    }

    function renderVideos(videos, rowId) {
        if (videos && videos.length > 0) {
            return (
                <div>
                    {videos.slice(0, 2).map((video, index) =>
                        video.video_value && (
                            <div key={`${rowId}-${index}`}>
                                <button
                                    className="imgContainer border-0 bg-transparent p-0"
                                    style={{width: "90px"}}
                                    onClick={() => setVideoModalShow(
                                        videoModalShow === video.video_value ? null : video.video_value,
                                    )}
                                >
                                    <div style={{position: "relative", width: "100%"}}>
                                        <video
                                            src={video.video_value}
                                            className="img-fluid rounded-1"
                                            playsInline={true}
                                            muted={true}
                                            preload="metadata"
                                            onLoadedMetadata={(e) => {
                                                e.target.currentTime = 0.1;
                                            }}
                                            style={{
                                                display: "block",
                                                width: "100%",
                                                borderRadius: "6px",
                                            }}
                                        >
                                        </video>
                                        <PlayCircleFilledIcon
                                            style={{
                                                position: "absolute",
                                                top: "50%",
                                                left: "50%",
                                                transform: "translate(-50%, -50%)",
                                                color: "red",
                                                fontSize: "2rem",
                                                zIndex: 10,
                                                pointerEvents: "none",
                                            }}
                                        />
                                    </div>
                                    {convertToEst(video.created_at, true)}
                                </button>
                                <Modal
                                    open={videoModalShow === video.video_value}
                                    onClose={() => setVideoModalShow(null)}
                                >
                                    <Box sx={mediaModalStyle}>
                                        <IconButton
                                            aria-label="close"
                                            size="large"
                                            onClick={() => setVideoModalShow(null)}
                                            sx={{
                                                position: "absolute",
                                                top: "12px",
                                                right: "15px",
                                                zIndex: "5000",
                                                color: theme.color.bodyBackground,
                                            }}
                                        >
                                            <CloseRoundedIcon
                                                sx={{
                                                    color: "red",
                                                    transform: "scale(1.4)",
                                                    stroke: "#333",
                                                    strokeWidth: 1.4,
                                                }}
                                            />
                                        </IconButton>
                                        <video
                                            className="w-100 rounded"
                                            src={video.video_value}
                                            controls={true}
                                            playsInline={true}
                                            preload="metadata"
                                            onLoadedMetadata={(e) => {
                                                e.target.currentTime = 0.1;
                                            }}
                                        ></video>
                                        {convertToEst(video.created_at)}
                                    </Box>
                                </Modal>
                            </div>
                        ))}
                </div>
            );
        } else {
            return "-";
        }
    }

    const columns = [
        {field: "index", headerName: "ID", width: 30, headerClassName: "header"},
        {
            field: "severity_object",
            headerName: "Issue & Severity",
            width: 200,
            headerClassName: "header",
            renderCell: (params) =>
                <>
                    <div className="d-flex flex-column">
                        {params.row.audit_type_item.name}
                        <div className="d-flex align-items-center mt-3">
                            <u>Severity Level:</u>
                            <CircleIcon fontSize="small" className="mx-2" sx={{color: params.value.color_code}} />
                            <strong>{params.value.name}</strong>
                        </div>
                    </div>
                </>,
        },
        {
            field: `${elementType}_images`,
            headerName: "Image",
            sortable: false,
            ...(isDesktop ? {flex: 3} : {width: 200}),
            headerClassName: "header",
            renderCell: (params) => {
                const images = params.value;
                return renderImages(images, params);
            },
        },
        {
            field: `${elementType}_videos`,
            headerName: "Video",
            sortable: false,
            ...(isDesktop ? {flex: 3} : {width: 200}),
            headerClassName: "header",
            renderCell: (params) => {
                const videos = params.value;
                const rowId = params.row.id;
                return renderVideos(videos, rowId);
            },
        },
        {
            field: "description",
            headerName: "Description",
            sortable: false,
            width: 200,
            headerClassName: "header",
            renderHeader: () => (
                <span>
                    Description
                    <SVPToolTipIcon
                        fontSize="20px"
                        placement="top"
                        message="This description box is visible to anyone you share it with—inside or outside
                            your organization."
                        rightMargin = "5px"
                    />
                </span>
            ),
            renderCell: (params) => params.value ? params.value : "-",
        },
        ...(!isPublicAudit ? [renderNotesCol()] : []),
        ...(elementType !== AnnotationType.ISSUE ? [renderDimension()] : []),
        ...(!isViewOnly ? [renderActions()] : []),
    ];

    return (
        <div className="auditTable my-5">
            <ConfirmationModal
                show={modalShow}
                onHide={() => setModalShow(false)}
                message={`Are you sure you want to delete this ${elementType}?`}
                onConfirm={handleDelete}
            />
            <GSPSPropDrivenTable
                title={headerName}
                data={items}
                columns={columns}
                disableColumnResize={true}
                disableColumnMenu={true}
                rowHeight="auto"
            />
            {!isDesktop && <div style={{color: "red", fontSize: "12px"}}>
                Scroll <ArrowRightAlt /> see all columns.
            </div>}
        </div>
    );
};


AuditTable.propTypes = {
    headerName: PropTypes.string.isRequired,
    dimensionHeader: PropTypes.string.isRequired,
    items: PropTypes.array,
    updateElementImages: PropTypes.func,
    updateElementVideos: PropTypes.func,
    isViewOnly: PropTypes.bool,
    elementType: PropTypes.string,
    onDelete: PropTypes.func,
    onEdit: PropTypes.func,
    onAddMedia: PropTypes.func,
    isPublicAudit: PropTypes.bool,
};

